import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownTrayIcon, DocumentTextIcon, PaperAirplaneIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { Button, Card, EmptyState, Loading, Modal, Property, Textarea } from "../../ui";
import { scholarshipsApi } from "../../../api/scholarships";
import { downloadExcel } from "../../../utils/excel";
import { printElement } from "../../../utils/print";
import { SCHOLARSHIPS_KEY, destinationOf, formatKigaliDate, fullName } from "../model";
import { formatDateTime, formatScore, openDocument } from "../review/model";

export const MAX_NOTE = 2000;

// "1 nominee goes" / "3 nominees go", for the send dialogs
export const nomineesGo = (count = 0) => (count === 1 ? "1 nominee goes" : `${count} nominees go`);

export const transmissionKey = (scholarshipId) => [...SCHOLARSHIPS_KEY, "transmission", scholarshipId];

// What the DG sends the sponsor: a preview (no reference yet) until the list is sent
export const useTransmission = (scholarshipId, enabled = true) =>
  useQuery({
    queryKey: transmissionKey(scholarshipId),
    enabled,
    queryFn: async () => {
      try {
        return (await scholarshipsApi.get(`staff/scholarships/${scholarshipId}/transmission`)).data;
      } catch (error) {
        console.error(`Failed to load the transmission of scholarship ${scholarshipId}:`, error);
        throw error;
      }
    },
  });

// The approval evidence the offer came from: the DG's letter for a private call, MINAFET's call otherwise
const originOf = (scholarship) => {
  const evidence = scholarship.approvalEvidence;
  const what =
    scholarship.visibility === "PRIVATE"
      ? "the Director General's letter approving the call for this scholarship"
      : "the call for applications received from the Ministry of Foreign Affairs and International Cooperation (MINAFET)";
  if (!evidence) return `Further to the call for applications for ${scholarship.name}`;
  return `Further to ${what} (${evidence.fileName}, received ${formatKigaliDate(evidence.uploadedAt)})`;
};

const awardListRows = (transmission) =>
  transmission.nominees.map((n, i) => ({
    No: i + 1,
    Nominee: fullName(n.applicant),
    Programme: n.programme?.name ?? "—",
    Level: n.level ?? "—",
    "HEC score": formatScore(n.meanScore),
  }));

const downloadAwardList = (scholarship, transmission) =>
  downloadExcel(
    awardListRows(transmission),
    `award-list-${scholarship.name}${transmission.reference ? `-${transmission.reference.replaceAll("/", "-")}` : ""}`,
    "Award list",
  );

/** The transmission letter and its award list (final scores only, no evaluators' marks), as the sponsor receives them. */
function TransmissionDocument({ scholarship, transmission, note, innerRef }) {
  const count = transmission.nominees.length;
  const shownNote = note ?? transmission.note;
  return (
    <article ref={innerRef} className="flex flex-col gap-5 text-sm leading-relaxed text-slate-800">
      <header className="flex flex-col gap-1 border-b border-slate-200 pb-4">
        <p className="text-base font-semibold text-slate-900">Higher Education Council</p>
        <p className="text-slate-600">Office of the Director General</p>
        <div className="mt-2 flex flex-wrap justify-between gap-2">
          <span>
            Ref: <span className="font-medium">{transmission.reference ?? "assigned when sent"}</span>
          </span>
          <span>Kigali, {formatKigaliDate(transmission.sentAt ?? new Date().toISOString())}</span>
        </div>
      </header>
      <div>
        <p>To: {scholarship.sponsor?.name ?? "The scholarship partner"}</p>
        <p className="mt-3 font-semibold">
          Subject: Transmission of nominees for {scholarship.name}
          {scholarship.academicYear?.name && ` (${scholarship.academicYear.name})`}
        </p>
      </div>
      <p>
        {originOf(scholarship)}, the Higher Education Council is pleased to transmit the {count === 1 ? "candidate" : `${count} candidates`} nominated for{" "}
        {scholarship.name}, tenable in {destinationOf(scholarship)}. The award list is attached below, and each nominee's application and documents are
        available to you in HECMIS.
      </p>
      {shownNote && <p className="whitespace-pre-line">{shownNote}</p>}
      <p>We kindly ask you to let us know whether each nominee is granted the scholarship.</p>
      <div>
        <p>Yours sincerely,</p>
        <p className="mt-6 font-semibold">{transmission.sentBy?.name ?? "The Director General"}</p>
        <p className="text-slate-600">Director General, Higher Education Council</p>
      </div>
      <section className="flex flex-col gap-2 border-t border-slate-200 pt-4">
        <p className="font-semibold text-slate-900">Annex: award list</p>
        {count === 0 ? (
          <p className="text-slate-500">Nobody is nominated.</p>
        ) : (
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                <th className="py-2 pr-3">No.</th>
                <th className="py-2 pr-3">Nominee</th>
                <th className="py-2 pr-3">Programme</th>
                <th className="py-2 pr-3">Level</th>
                <th className="py-2">HEC score</th>
              </tr>
            </thead>
            <tbody>
              {transmission.nominees.map((n, i) => (
                <tr key={n.applicationId} className="border-b border-slate-100">
                  <td className="py-2 pr-3">{i + 1}</td>
                  <td className="py-2 pr-3">{fullName(n.applicant)}</td>
                  <td className="py-2 pr-3">{n.programme?.name ?? "—"}</td>
                  <td className="py-2 pr-3">{n.level ?? "—"}</td>
                  <td className="py-2">{formatScore(n.meanScore)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </article>
  );
}

/**
 * The letter and award list in a dialog. With `onSend` it is the DG's preview before Approve and
 * send, with a note the letter shows as it is typed; without, it shows what was sent.
 */
export function TransmissionDialog({ scholarship, open, onClose, onSend, busy = false, description }) {
  const [note, setNote] = useState("");
  const letter = useRef(null);
  const { data: transmission, isPending, isError, refetch } = useTransmission(scholarship.id, open);
  const preview = Boolean(onSend);

  const footer = (
    <>
      <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
        {preview ? "Cancel" : "Close"}
      </Button>
      {transmission && (
        <>
          <Button
            variant="outline"
            size="sm"
            icon={<ArrowDownTrayIcon className="h-4 w-4" />}
            onClick={() => downloadAwardList(scholarship, transmission)}
            disabled={busy}
          >
            Award list
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<PrinterIcon className="h-4 w-4" />}
            onClick={() => printElement(letter.current, transmission.reference ?? scholarship.name)}
            disabled={busy}
          >
            Print
          </Button>
        </>
      )}
      {preview && (
        <Button size="sm" icon={<PaperAirplaneIcon className="h-4 w-4" />} onClick={() => onSend(note.trim())} loading={busy} disabled={!transmission}>
          Approve and send
        </Button>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      dismissible={!busy}
      title={preview ? "Preview what the partner receives" : "Sent to the partner"}
      description={description}
      footer={footer}
    >
      {isPending ? (
        <Loading label="Loading the transmission…" />
      ) : isError ? (
        <EmptyState title="Could not load the transmission" onRefresh={refetch} />
      ) : (
        <div className="flex flex-col gap-4">
          {preview && (
            <Textarea
              label="Note to the partner (optional)"
              rows={2}
              maxLength={MAX_NOTE}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
          )}
          <div className="rounded-lg border border-slate-200 p-5">
            <TransmissionDocument scholarship={scholarship} transmission={transmission} note={preview ? note.trim() : null} innerRef={letter} />
          </div>
          {scholarship.approvalEvidence && (
            <p className="text-sm text-slate-600">
              Linked evidence:{" "}
              <button
                type="button"
                className="font-medium text-brand hover:underline"
                onClick={() => openDocument(`staff/scholarships/${scholarship.id}/approval-evidence/download-url`)}
              >
                {scholarship.approvalEvidence.fileName}
              </button>
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

// The sponsor's record of what HEC sent: the letter, the award list and the evidence the offer came from
export function TransmissionCard({ scholarship }) {
  const [viewing, setViewing] = useState(false);
  const { data: transmission, isPending, isError } = useTransmission(scholarship.id);

  if (isPending || isError || !transmission?.reference) return null;
  return (
    <Card
      title="Sent by HEC"
      subtitle="The transmission letter and award list. Each nominee's application and documents open from View in the list below."
      actions={
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" icon={<DocumentTextIcon className="h-4 w-4" />} onClick={() => setViewing(true)}>
            Letter and award list
          </Button>
          <Button size="sm" variant="outline" icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={() => downloadAwardList(scholarship, transmission)}>
            Award list
          </Button>
        </div>
      }
    >
      <div className="grid gap-x-10 md:grid-cols-2">
        <Property label="Reference" value={transmission.reference} />
        <Property label="Sent" value={`${formatDateTime(transmission.sentAt)}${transmission.sentBy?.name ? ` by ${transmission.sentBy.name}` : ""}`} />
        <Property label="Nominees" value={transmission.nominees.length} />
      </div>
      <TransmissionDialog scholarship={scholarship} open={viewing} onClose={() => setViewing(false)} />
    </Card>
  );
}
